import { useSubscription } from "@/context/SubscriptionContext"
import { getPlans, getSubscriptionLimits, getSubscriptionHistory } from "@/api/kuizy"
import { STATUS_LABEL, STATUS_COLOR, trialDaysLeft } from "@/utils/subscription"
import { useEffect, useState } from "react"
import { formatDateTime } from "@/settings/master"
import { Check } from "lucide-react"
import CheckoutFlow from "@/components/checkout/CheckoutFlow"

function StatusBadge({ status }) {
  const color = STATUS_COLOR[status] || "gray"
  const colorMap = {
    yellow: "bg-yellow-100 text-yellow-700",
    green: "bg-green-100 text-green-700",
    red: "bg-red-100 text-red-700",
    gray: "bg-gray-100 text-gray-700",
    orange: "bg-orange-100 text-orange-700",
  }
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${colorMap[color] || colorMap.gray}`}>
      {STATUS_LABEL[status] || status}
    </span>
  )
}

function SubscriptionStatus({ subscription }) {
  if (!subscription) return null
  const daysLeft = trialDaysLeft(subscription)

  return (
    <div className="mb-6">
      <p className="text-xs text-gray-500 font-medium mb-2 uppercase tracking-wide">สถานะปัจจุบัน</p>
      <div className="border border-gray-200 rounded-xl p-5">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 text-sm divide-x divide-gray-100">
          <div className="pr-4">
            <p className="text-gray-400 text-xs mb-1">แผนปัจจุบัน</p>
            <p className="font-bold text-base">{subscription.display_name || subscription.plan_name}</p>
          </div>
          <div className="px-4">
            <p className="text-gray-400 text-xs mb-1">สถานะ</p>
            <StatusBadge status={subscription.status} />
          </div>
          {subscription.status === "trialing" && (
            <div className="px-4">
              <p className="text-gray-400 text-xs mb-1">ทดลองใช้เหลือ</p>
              <p className="font-bold text-base">{daysLeft} วัน</p>
            </div>
          )}
          {subscription.current_period_start && (
            <div className="px-4">
              <p className="text-gray-400 text-xs mb-1">เริ่มใช้งาน</p>
              <p className="font-bold text-base">{formatDateTime(subscription.current_period_start)}</p>
            </div>
          )}
          {subscription.current_period_end && (
            <div className="px-4">
              <p className="text-gray-400 text-xs mb-1">ต่ออายุถัดไป</p>
              <p className="font-bold text-base">{formatDateTime(subscription.current_period_end)}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function UsageCard({ label, used, limit }) {
  const pct = limit === -1 ? 0 : Math.min((used / limit) * 100, 100)
  const over = limit !== -1 && used >= limit
  const hasUsage = used > 0

  return (
    <div className="border border-gray-200 rounded-xl p-4">
      <div className="flex justify-between items-start mb-1">
        <p className="font-semibold text-sm">{label}</p>
        <p className={`text-sm font-semibold ${over ? "text-red-600" : "text-gray-700"}`}>
          {used} / {limit === -1 ? "∞" : limit}
        </p>
      </div>
      {hasUsage ? (
        <p className="text-xs text-gray-400 mb-2">
          ใช้ไป {pct.toFixed(0)}% ของโควต้า
        </p>
      ) : (
        <p className="text-xs text-gray-400 mb-2">ยังไม่มีการใช้งาน</p>
      )}
      {limit !== -1 && (
        <div className="h-1.5 bg-gray-100 rounded-full">
          <div
            className={`h-1.5 rounded-full transition-all ${over ? "bg-red-500" : "bg-pink-500"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  )
}

function UsageLimitsSection({ limits }) {
  if (!limits) return null
  const items = [
    { label: "Admins", ...limits.admins },
    { label: "Creators", ...limits.creators },
    { label: "Learners", ...limits.learners },
    { label: "Courses", ...limits.courses },
    { label: "Exams", ...limits.exams },
    { label: "Questions", ...limits.questions },
  ]
  return (
    <div className="mb-6">
      <p className="text-xs text-gray-500 font-medium mb-2 uppercase tracking-wide">การใช้งาน</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map(item => <UsageCard key={item.label} {...item} />)}
      </div>
    </div>
  )
}

function PlanCard({ plan, current, onUpgrade, isPopular }) {
  const isCurrent = current?.plan_id === plan.id
  const isFree = plan.price_monthly === 0

  return (
    <div className={`relative rounded-xl p-5 flex flex-col transition-all
      ${isCurrent
        ? "border-2 border-blue-500 bg-white"
        : isPopular
          ? "border-2 border-pink-500 bg-white"
          : "border border-gray-200 bg-white"
      }`}
    >
      {/* Badge */}
      {isCurrent && (
        <span className="absolute -top-3 left-4 bg-blue-500 text-white text-xs font-semibold px-3 py-0.5 rounded-full">
          แผนปัจจุบัน
        </span>
      )}
      {isPopular && !isCurrent && (
        <span className="absolute -top-3 left-4 bg-pink-500 text-white text-xs font-semibold px-3 py-0.5 rounded-full">
          ยอดนิยม
        </span>
      )}

      <p className="text-xs text-gray-400 mb-3">{plan.description || (isFree ? "Free Plan" : plan.display_name)}</p>
      <div className="mb-4">
        {isFree ? (
          <div className="text-3xl font-bold">
            ฟรี <span className="text-base font-normal text-gray-500">7 วัน</span>
          </div>
        ) : (
          <div className="text-3xl font-bold">
            ฿{plan.price_monthly?.toLocaleString()}
            <span className="text-base font-normal text-gray-500"> / เดือน</span>
          </div>
        )}
      </div>
      <hr className="border-gray-200 mb-4" />
      <ul className="space-y-2 flex-1 text-sm mb-5">
        {[
          { label: "Admins", val: plan.max_admins },
          { label: "Creators", val: plan.max_creators },
          { label: "Learners", val: plan.max_learners },
          { label: "Courses", val: plan.max_courses },
          { label: "Exams", val: plan.max_exams },
          { label: "Questions", val: plan.max_questions },
        ].map(({ label, val }) => (
          <li key={label} className="flex items-center gap-2 text-gray-700">
            <span className="p-1 bg-green-100 rounded-full">
              <Check size={14} className="text-green-500 font-bold shrink-0" />
            </span>
            {label}: {val === -1 ? "ไม่จำกัด" : val}
          </li>
        ))}
      </ul>

      {isCurrent ? (
        <button disabled className="w-full py-2 rounded-lg bg-gray-100 text-gray-400 text-sm cursor-not-allowed">
          แผนปัจจุบัน
        </button>
      ) : (
        <button
          onClick={() => onUpgrade(plan.id)}
          className={`w-full py-2 rounded-lg text-sm font-semibold transition
            ${isPopular
              ? "bg-pink-500 text-white hover:bg-pink-600"
              : "border border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
        >
          เลือกแผนนี้
        </button>
      )}
    </div>
  )
}

function HistoryTable({ history }) {
  if (!history?.length) return null
  return (
    <div className="mt-8">
      <p className="text-xs text-gray-500 font-medium mb-3 uppercase tracking-wide">ประวัติ SUBSCRIPTION</p>
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-gray-500 font-medium">วันที่</th>
              <th className="px-4 py-3 text-left text-gray-500 font-medium">การเปลี่ยนแปลง</th>
              <th className="px-4 py-3 text-left text-gray-500 font-medium">แผน</th>
              <th className="px-4 py-3 text-right text-gray-500 font-medium">จำนวนเงิน</th>
            </tr>
          </thead>
          <tbody>
            {history.map((h, i) => (
              <tr key={i} className={`border-t border-gray-100 ${i % 2 === 0 ? "" : "bg-gray-50/40"}`}>
                <td className="px-4 py-3 text-gray-600">{formatDateTime(h.created_at)}</td>
                <td className="px-4 py-3 text-gray-700">{h.change_type}</td>
                <td className="px-4 py-3">
                  <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs font-medium">
                    {h.plan_name}
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-gray-600">
                  {h.amount_paid ? `฿${h.amount_paid.toLocaleString()}` : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default function SubscriptionPage() {
  const { subscription, refresh } = useSubscription()
  const [plans, setPlans] = useState([])
  const [limits, setLimits] = useState(null)
  const [history, setHistory] = useState([])
  const [checkoutPlan, setCheckoutPlan] = useState(null)

  useEffect(() => {
    getPlans().then(r => setPlans(r.data || [])).catch(() => { })
    getSubscriptionLimits().then(r => setLimits(r.data || null)).catch(() => { })
    getSubscriptionHistory().then(r => setHistory(r.data || [])).catch(() => { })
  }, [])

  async function handleCheckoutSuccess() {
    await refresh()
    const limRes = await getSubscriptionLimits()
    setLimits(limRes.data || null)
    getSubscriptionHistory().then(r => setHistory(r.data || [])).catch(() => { })
  }

  // Mark 2nd non-free plan as popular (index 1 in paid plans = index 2 overall typically)
  const popularPlanId = plans.filter(p => p.price_monthly > 0)[1]?.id

  return (
    <div className="mx-auto py-2">
      {checkoutPlan ? (
        <CheckoutFlow
          plan={checkoutPlan}
          onClose={() => setCheckoutPlan(null)}
          onSuccess={handleCheckoutSuccess}
        />
      ) : (
        <>
          <h2 className="text-xl font-bold mb-1">Subscription</h2>
          <p className="text-sm text-gray-500 mb-6">จัดการแผนการใช้งานและดูสถานะการใช้งานของ workspace คุณ</p>

          <SubscriptionStatus subscription={subscription} />

          <UsageLimitsSection limits={limits} />

          <div className="mb-6">
            <p className="text-xs text-gray-500 font-medium mb-3 uppercase tracking-wide">เลือกแผน</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-3">
              {plans.map(plan => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  current={subscription}
                  onUpgrade={(id) => {
                    const p = plans.find(pl => pl.id === id)
                    if (p) setCheckoutPlan(p)
                  }}
                  isPopular={plan.id === popularPlanId}
                />
              ))}
            </div>
          </div>

          <HistoryTable history={history} />
        </>
      )}
    </div>
  )
}
